/**
 * CityReviewsBlock — city-tagged Google reviews for service-area pages.
 * "What patients near {City} say…" — boosts local trust + conversion.
 * Reuses the working /api/reviews/google endpoint (server-cached).
 */
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ n = 5 }) => (
  <div className="flex text-yellow-400" aria-hidden="true">
    {[...Array(5)].map((_, i) => (
      <span key={i} className={i < n ? 'text-yellow-400' : 'text-gray-300'}>★</span>
    ))}
  </div>
);

const CityReviewsBlock = ({ city }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/reviews/google`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data || !data.reviews) return null;
  const top = data.reviews.filter((r) => r.rating >= 5).slice(0, 3);
  if (top.length === 0) return null;

  return (
    <section className="bg-white py-12 border-t border-gray-100" data-testid="city-reviews-block">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
            What Patients Near {city} Say
          </h2>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Stars n={Math.round(data.rating)} />
            <span className="text-xl font-bold text-gray-800">{data.rating}</span>
            <span className="text-gray-500">· {data.total_reviews}+ Google reviews</span>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {top.map((review, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-5 shadow-sm" data-testid={`city-review-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold">
                  {review.author_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-sm">{review.author_name}</div>
                  <span className="text-xs text-gray-500">{review.relative_time_description}</span>
                </div>
              </div>
              <Stars n={review.rating} />
              <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                "{review.text.length > 180 ? review.text.substring(0, 180) + '…' : review.text}"
              </p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <a
            href="https://g.page/r/CUolB0PBcru6EBM/review"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="city-reviews-google-cta"
            className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Read more reviews from {city}-area patients →
          </a>
        </div>
      </div>
    </section>
  );
};

export default CityReviewsBlock;
